import React, { useState, useEffect } from 'react';
import { Card, Tabs, Table, Tag, Button, Row, Col, Statistic, Space, Select, message, Badge } from 'antd';
import { WarningOutlined, ClockCircleOutlined, CheckCircleOutlined, TeamOutlined, BarChartOutlined, GlobalOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import MapaClientes from '../components/MapaClientes';

const { Option } = Select;

const fmtData = (v) => v ? new Date(v).toLocaleDateString('pt-BR') : '-';
const fmtMoeda = (v) => v ? 'R$ ' + parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : 'R$ 0,00';

const STATUS_LABELS = { rascunho: 'Rascunho', proposta_enviada: 'Enviada', oc_recebida: 'OC Recebida', agendado: 'Agendado', inspecionado: 'Inspecionado', laudo_emitido: 'Laudo Emitido', nf_emitida: 'NF Emitida' };
const STATUS_CORES = { rascunho: 'default', proposta_enviada: 'blue', oc_recebida: 'cyan', agendado: 'purple', inspecionado: 'orange', laudo_emitido: 'green', nf_emitida: 'gold' };

export default function Gestao() {
  const [laudos, setLaudos] = useState({ vencidos: [], vencendo30: [], vencendo60: [], vencendo90: [] });
  const [propostas, setPropostas] = useState([]);
  const [mapaClientes, setMapaClientes] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState(6);
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const [rLaudos, rPropostas, rMapa] = await Promise.all([
        api.get('/gestao/laudos'),
        api.get('/gestao/propostas'),
        api.get('/gestao/mapa'),
      ]);
      setLaudos(rLaudos.data);
      setPropostas(rPropostas.data);
      setMapaClientes(rMapa.data);
    } catch { message.error('Erro ao carregar dados de gestão'); }
    finally { setLoading(false); }
  };

  const colunasLaudo = (cor) => [
    { title: 'Código', dataIndex: 'codigo', key: 'codigo', width: 110 },
    { title: 'TAG', dataIndex: 'tag', key: 'tag', width: 80 },
    { title: 'Cliente', key: 'cliente', render: (_, r) => r.razao_faturamento || r.razao_social || r.nome_cliente },
    { title: 'Cidade', key: 'cidade', render: (_, r) => r.end_cidade ? r.end_cidade + '/' + r.end_estado : '-', width: 120 },
    { title: 'Próx. Insp. Externa', dataIndex: 'proxima_inspecao_externa', key: 'venc', width: 150, render: v => <span style={{ color: cor, fontWeight: 'bold' }}>{fmtData(v)}</span> },
    { title: 'Ações', key: 'acoes', width: 100, render: (_, r) => (
      <Space>
        <Button size="small" onClick={() => navigate('/laudos/' + r.id + '/editar')}>Ver</Button>
        <Button size="small" type="primary" onClick={() => navigate('/clientes/' + r.cliente_id)}>Cliente</Button>
      </Space>
    )},
  ];

  // Dados para gráfico de propostas por mês
  const mesesDisponiveis = [...new Set(propostas.map(p => p.mes))].sort().reverse().slice(0, filtroPeriodo).reverse();
  const usuariosDisponiveis = [...new Set(propostas.map(p => p.criado_por).filter(Boolean))];
  
  const STATUS_APROVADOS = ['oc_recebida', 'agendado', 'inspecionado', 'laudo_emitido', 'nf_emitida'];
  const dadosGraficoAprovadas = mesesDisponiveis.map(mes => {
    const doMes = propostas.filter(p => p.mes === mes && (!filtroUsuario || p.criado_por === filtroUsuario));
    const aprovadas = doMes.filter(p => STATUS_APROVADOS.includes(p.status));
    const qtd = aprovadas.reduce((s, p) => s + parseInt(p.quantidade), 0);
    const valor = aprovadas.reduce((s, p) => s + parseFloat(p.valor_total || 0), 0);
    return { mes: mes.substring(5) + '/' + mes.substring(0, 4), aprovadas: qtd, valor };
  });

  const dadosGrafico = mesesDisponiveis.map(mes => {
    const doMes = propostas.filter(p => p.mes === mes && (!filtroStatus || p.status === filtroStatus) && (!filtroUsuario || p.criado_por === filtroUsuario));
    const total = doMes.reduce((s, p) => s + parseInt(p.quantidade), 0);
    const valor = doMes.reduce((s, p) => s + parseFloat(p.valor_total || 0), 0);
    return { mes: mes.substring(5) + '/' + mes.substring(0, 4), total, valor };
  });

  // Cidades únicas para filtro do mapa
  const cidadesUnicas = [...new Set(mapaClientes.map(c => c.cidade).filter(Boolean))].sort();
  const [filtroCidade, setFiltroCidade] = useState('');
  const clientesFiltrados = filtroCidade ? mapaClientes.filter(c => c.cidade === filtroCidade) : mapaClientes;

  const colunasMapa = [
    { title: 'Cliente', key: 'cliente', render: (_, r) => <><div><strong>{r.razao_filial || r.razao_social}</strong></div><div style={{ fontSize: 11, color: '#888' }}>{r.cnpj}</div></> },
    { title: 'Cidade', key: 'cidade', render: (_, r) => r.cidade + '/' + r.estado },
    { title: 'Endereço', key: 'end', render: (_, r) => [r.logradouro, r.numero].filter(Boolean).join(', ') || '-' },
    { title: 'Ações', key: 'acoes', width: 80, render: (_, r) => <Button size="small" onClick={() => navigate('/clientes/' + r.id)}>Ver</Button> },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Área de Gestão</h2>
        <p style={{ color: '#888', margin: 0 }}>Controle de laudos, propostas e clientes</p>
      </div>

      <Tabs defaultActiveKey="laudos" items={[
        {
          key: 'laudos',
          label: <span><WarningOutlined /> Laudos</span>,
          children: (
            <div>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}><Card><Statistic title="Vencidos" value={laudos.vencidos?.length || 0} valueStyle={{ color: '#cf1322' }} prefix={<WarningOutlined />} /></Card></Col>
                <Col span={6}><Card><Statistic title="Vencendo em 30 dias" value={laudos.vencendo30?.length || 0} valueStyle={{ color: '#fa8c16' }} prefix={<ClockCircleOutlined />} /></Card></Col>
                <Col span={6}><Card><Statistic title="Vencendo em 60 dias" value={laudos.vencendo60?.length || 0} valueStyle={{ color: '#faad14' }} prefix={<ClockCircleOutlined />} /></Card></Col>
                <Col span={6}><Card><Statistic title="Vencendo em 90 dias" value={laudos.vencendo90?.length || 0} valueStyle={{ color: '#1890ff' }} prefix={<CheckCircleOutlined />} /></Card></Col>
              </Row>

              {laudos.vencidos?.length > 0 && (
                <Card size="small" title={<span style={{ color: '#cf1322' }}>⚠️ Laudos Vencidos ({laudos.vencidos.length})</span>} style={{ marginBottom: 16, borderColor: '#ffccc7' }}>
                  <Table columns={colunasLaudo('#cf1322')} dataSource={laudos.vencidos} rowKey="id" size="small" pagination={{ pageSize: 5 }} />
                </Card>
              )}
              {laudos.vencendo30?.length > 0 && (
                <Card size="small" title={<span style={{ color: '#fa8c16' }}>🕐 Vencendo em 30 dias ({laudos.vencendo30.length})</span>} style={{ marginBottom: 16, borderColor: '#ffd591' }}>
                  <Table columns={colunasLaudo('#fa8c16')} dataSource={laudos.vencendo30} rowKey="id" size="small" pagination={{ pageSize: 5 }} />
                </Card>
              )}
              {laudos.vencendo60?.length > 0 && (
                <Card size="small" title={<span style={{ color: '#faad14' }}>🕐 Vencendo em 60 dias ({laudos.vencendo60.length})</span>} style={{ marginBottom: 16, borderColor: '#ffe58f' }}>
                  <Table columns={colunasLaudo('#faad14')} dataSource={laudos.vencendo60} rowKey="id" size="small" pagination={{ pageSize: 5 }} />
                </Card>
              )}
              {laudos.vencendo90?.length > 0 && (
                <Card size="small" title={<span style={{ color: '#1890ff' }}>📅 Vencendo em 90 dias ({laudos.vencendo90.length})</span>} style={{ marginBottom: 16 }}>
                  <Table columns={colunasLaudo('#1890ff')} dataSource={laudos.vencendo90} rowKey="id" size="small" pagination={{ pageSize: 5 }} />
                </Card>
              )}
              {!laudos.vencidos?.length && !laudos.vencendo30?.length && !laudos.vencendo60?.length && !laudos.vencendo90?.length && (
                <Card><p style={{ textAlign: 'center', color: '#52c41a' }}>✅ Nenhum laudo vencido ou vencendo nos próximos 90 dias!</p></Card>
              )}
            </div>
          )
        },
        {
          key: 'propostas',
          label: <span><BarChartOutlined /> Propostas</span>,
          children: (
            <div>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                  <Select placeholder="Filtrar por status" style={{ width: '100%' }} allowClear value={filtroStatus || undefined} onChange={v => setFiltroStatus(v || '')}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
                  </Select>
                </Col>
                <Col span={6}>
                  <Select placeholder="Filtrar por usuário" style={{ width: '100%' }} allowClear value={filtroUsuario || undefined} onChange={v => setFiltroUsuario(v || '')}>
                    {usuariosDisponiveis.map(u => <Option key={u} value={u}>{u}</Option>)}
                  </Select>
                </Col>
                <Col span={6}>
                  <Select value={filtroPeriodo} onChange={v => setFiltroPeriodo(v)} style={{ width: '100%' }}>
                    <Option value={3}>Últimos 3 meses</Option>
                    <Option value={6}>Últimos 6 meses</Option>
                    <Option value={12}>Últimos 12 meses</Option>
                  </Select>
                </Col>
              </Row>
              <Card size="small" title="Propostas por mês (últimos 6 meses)" style={{ marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dadosGrafico}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={v => 'R$' + (v/1000).toFixed(0) + 'k'} />
                    <Tooltip formatter={(v, n) => n === 'valor' ? fmtMoeda(v) : v} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="total" name="Quantidade" fill="#0099FF" />
                    <Bar yAxisId="right" dataKey="valor" name="Valor Total" fill="#52c41a" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card size="small" title="Propostas Aprovadas por mês (OC recebida ou superior)" style={{ marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dadosGraficoAprovadas}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={v => 'R$' + (v/1000).toFixed(0) + 'k'} />
                    <Tooltip formatter={(v, n) => n === 'valor' ? fmtMoeda(v) : v} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="aprovadas" name="Aprovadas" fill="#52c41a" />
                    <Bar yAxisId="right" dataKey="valor" name="Valor Aprovado" fill="#0099FF" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
          )
        },
        {
          key: 'mapa',
          label: <span><GlobalOutlined /> Mapa de Clientes</span>,
          children: (
            <div>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                  <Select placeholder="Filtrar por cidade" style={{ width: '100%' }} allowClear value={filtroCidade || undefined} onChange={v => setFiltroCidade(v || '')}>
                    {cidadesUnicas.map(c => <Option key={c} value={c}>{c}</Option>)}
                  </Select>
                </Col>
                <Col span={4}>
                  <Tag color="blue">{clientesFiltrados.length} registros</Tag>
                </Col>
              </Row>
              <Card size="small" style={{ marginBottom: 16 }}>
                <MapaClientes clientes={clientesFiltrados} onRecarregar={carregar} />
              </Card>
              <Card size="small" title={`CNPJs cadastrados — ${clientesFiltrados.length} registros`}>
                <Table columns={colunasMapa} dataSource={clientesFiltrados} rowKey={(r, i) => i} size="small" pagination={{ pageSize: 10 }} />
              </Card>
            </div>
          )
        }
      ]} />
    </div>
  );
}
